
import { useState, useEffect } from 'react';
import { BigFiveAnswer, BigFiveQuestion, TestVersion } from '@/types/personality';
import { bigFiveQuestions, bigFiveQuestionsShort } from '@/data/bigFiveQuestions';

interface UseBigFiveTestProps {
  version?: TestVersion;
  initialAnswers?: BigFiveAnswer[];
}

export const useBigFiveTest = ({ 
  version = 'full', 
  initialAnswers = [] 
}: UseBigFiveTestProps = {}) => {
  // Get questions based on test version
  const [questions, setQuestions] = useState<BigFiveQuestion[]>(
    version === 'full' ? bigFiveQuestions : bigFiveQuestionsShort
  );
  
  // Current question index
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // User answers
  const [answers, setAnswers] = useState<BigFiveAnswer[]>(initialAnswers);
  
  // Test completion state
  const [isCompleted, setIsCompleted] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update questions when version changes
  useEffect(() => {
    setQuestions(version === 'full' ? bigFiveQuestions : bigFiveQuestionsShort);
  }, [version]);

  // Calculate progress - ensure it never exceeds 100%
  const progress = questions.length > 0 
    ? Math.min(100, Math.round((answers.length / questions.length) * 100))
    : 0;

  // Current question
  const currentQuestion = questions[currentIndex];

  // Get answer for current question if exists
  const getCurrentAnswer = () => {
    return answers.find(a => a.questionId === currentQuestion?.id)?.score || 0;
  };

  // Answer current question
  const answerQuestion = (score: number) => {
    if (!currentQuestion) return;
    
    // Find if this question was already answered
    const existingIndex = answers.findIndex(a => a.questionId === currentQuestion.id);
    
    // Create new answers array, either updating or adding the answer
    const newAnswers = [...answers];
    if (existingIndex >= 0) {
      newAnswers[existingIndex] = { questionId: currentQuestion.id, score };
    } else {
      newAnswers.push({ questionId: currentQuestion.id, score });
    }
    
    setAnswers(newAnswers);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('bigFiveAnswers', JSON.stringify(newAnswers));
    } catch (e) {
      console.error('Failed to save answers to localStorage', e);
    }
    
    // Move to next question if not at the end
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsCompleted(true);
    }
  };
  
  // Go to previous question
  const previousQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  // Go to specific question
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
    }
  };
  
  // Reset the test
  const resetTest = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setIsCompleted(false);
    localStorage.removeItem('bigFiveAnswers');
  };
  
  // Load saved answers from localStorage
  useEffect(() => {
    try {
      const savedAnswers = localStorage.getItem('bigFiveAnswers');
      if (savedAnswers) {
        const parsedAnswers = JSON.parse(savedAnswers) as BigFiveAnswer[];
        setAnswers(parsedAnswers);
      }
    } catch (e) {
      console.error('Failed to load saved answers', e);
    }
  }, []);
  
  return {
    questions,
    currentQuestion,
    currentIndex,
    answers,
    isCompleted,
    isLoading,
    error,
    progress,
    answerQuestion,
    previousQuestion,
    goToQuestion,
    resetTest,
    getCurrentAnswer
  };
};
